/**
 * O estado de uma rotina num alvo — DERIVADO das linhas append-only, nunca
 * coluna (`api.md` 3; F3 da revisão de out/2026, ADR-0026).
 *
 * Função pura, sem banco: a API a chama com o que leu, e o spec a percorre
 * caso a caso (regra escondida em service vira função pura — `testes.md`).
 * O desenho está em `F3-desenho.md` §2 do plano de revisão.
 */

import type { ClasseDeEfeito } from '@remoto/protocolo';

/**
 * Os estados, na ORDEM em que a regra os decide (a precedência de
 * `estadoNoAlvo`). `sem_versao_aprovada` é a rotina que só tem proposta: nada
 * roda, e a tela não pode confundi-la com "não se aplica a este alvo".
 */
export const ESTADOS_NO_ALVO = [
  'sem_versao_aprovada',
  'quebrada',
  'nao_se_aplica',
  'suspeita',
  'validada',
  'so_ensaio',
] as const;
export type EstadoNoAlvo = (typeof ESTADOS_NO_ALVO)[number];

/** Datas como vêm do banco (`Date`) ou do fio (ISO). */
type Instante = Date | string;

export interface DecisaoLida {
  decisao: 'APROVADA' | 'RECUSADA';
  em: Instante;
}

export interface VersaoLida {
  id: string;
  numero: number;
  decisoes: readonly DecisaoLida[];
}

export interface ExtensaoLida {
  alvoId: string;
  decisoes: readonly DecisaoLida[];
}

export interface ValidacaoLida {
  versaoId: string;
  alvoId: string;
  tipo: 'ENSAIO' | 'REAL' | 'SONDA';
  resultado: 'CONFERIDO' | 'FALHOU' | 'NAO_CONFERIDO' | 'PENDENCIA_HUMANA' | 'PAROU';
  falhaDe: 'ALVO' | 'AMBIENTE' | 'ROTINA' | null;
  em: Instante;
}

export interface MarcacaoLida {
  alvoId: string;
  marca: 'QUEBRADA' | 'REVISADA';
  em: Instante;
}

/** O que a regra precisa saber de UMA rotina para decidir o estado em qualquer alvo. */
export interface HistoricoDaRotina {
  versoes: readonly VersaoLida[];
  extensoes: readonly ExtensaoLida[];
  validacoes: readonly ValidacaoLida[];
  marcacoes: readonly MarcacaoLida[];
}

const ms = (i: Instante): number => (i instanceof Date ? i.getTime() : Date.parse(i));

/** A última decisão de UMA proposta — append-only, então a mais recente é a que vale. */
function ultimaDecisao(decisoes: readonly DecisaoLida[]): DecisaoLida | null {
  let ultima: DecisaoLida | null = null;
  for (const d of decisoes) if (ultima === null || ms(d.em) >= ms(ultima.em)) ultima = d;
  return ultima;
}

/**
 * A versão VIGENTE: a de decisão `APROVADA` mais recente. Voltar atrás é
 * aprovar uma versão nova com o manifesto antigo (R5), então "a mais recente
 * aprovada" basta — nunca "a de número maior". Empate de instante: o número
 * maior, para a regra não depender da ordem em que o banco devolve as linhas.
 */
export function versaoVigente(versoes: readonly VersaoLida[]): VersaoLida | null {
  let vigente: VersaoLida | null = null;
  let quando = -Infinity;
  for (const v of versoes) {
    const d = ultimaDecisao(v.decisoes);
    if (d?.decisao !== 'APROVADA') continue;
    const t = ms(d.em);
    if (t > quando || (t === quando && vigente !== null && v.numero > vigente.numero)) {
      vigente = v;
      quando = t;
    }
  }
  return vigente;
}

/** Há extensão APROVADA da rotina para este alvo? (por rotina — F3-desenho §3) */
export function extensaoAprovada(extensoes: readonly ExtensaoLida[], alvoId: string): boolean {
  return extensoes.some((e) => e.alvoId === alvoId && ultimaDecisao(e.decisoes)?.decisao === 'APROVADA');
}

/**
 * A validação conta para a SUSPEITA? Só o que é conclusivo sobre a rotina ou o
 * alvo: `CONFERIDO`, ou `FALHOU` com a culpa do `ALVO` ou da `ROTINA`. Falha
 * de `AMBIENTE` não suspende (D32, R16); `NAO_CONFERIDO`, `PENDENCIA_HUMANA` e
 * `PAROU` não dizem nada de nenhum dos dois.
 */
function conclusiva(v: ValidacaoLida): boolean {
  if (v.resultado === 'CONFERIDO') return true;
  return v.resultado === 'FALHOU' && (v.falhaDe === 'ALVO' || v.falhaDe === 'ROTINA');
}

/**
 * O estado da rotina NESTE alvo, pela precedência de `F3-desenho.md` §2:
 *
 * 0. `sem_versao_aprovada` — só há proposta;
 * 1. `quebrada` — a última marca humana do alvo é `QUEBRADA` (`REVISADA` a desfaz);
 * 2. `nao_se_aplica` — nenhuma extensão aprovada para o alvo;
 * 3. `suspeita` — a última validação REAL ou SONDA CONCLUSIVA da vigente no alvo
 *    é uma falha do alvo ou da rotina. Uma falha NUNCA troca a versão (D32);
 * 4. `validada` — há REAL `CONFERIDO` da VIGENTE no alvo;
 * 5. `so_ensaio` — o resto.
 *
 * **Sem herança entre versões na fase 1:** a versão nova começa `so_ensaio` em
 * todo alvo até o próprio real conferido ("família não dispensa validação",
 * D31, vale entre versões). Para rotina de leitura não há impasse: a leitura
 * real é permitida em `so_ensaio` (`podeRodar`).
 */
export function estadoNoAlvo(historico: HistoricoDaRotina, alvoId: string): EstadoNoAlvo {
  const vigente = versaoVigente(historico.versoes);
  if (vigente === null) return 'sem_versao_aprovada';

  let marca: MarcacaoLida | null = null;
  for (const m of historico.marcacoes) {
    if (m.alvoId === alvoId && (marca === null || ms(m.em) >= ms(marca.em))) marca = m;
  }
  if (marca?.marca === 'QUEBRADA') return 'quebrada';

  if (!extensaoAprovada(historico.extensoes, alvoId)) return 'nao_se_aplica';

  const daVigente = historico.validacoes.filter((v) => v.versaoId === vigente.id && v.alvoId === alvoId);
  let decisiva: ValidacaoLida | null = null;
  for (const v of daVigente) {
    if ((v.tipo === 'REAL' || v.tipo === 'SONDA') && conclusiva(v) && (decisiva === null || ms(v.em) >= ms(decisiva.em))) {
      decisiva = v;
    }
  }
  if (decisiva?.resultado === 'FALHOU') return 'suspeita';

  return daVigente.some((v) => v.tipo === 'REAL' && v.resultado === 'CONFERIDO') ? 'validada' : 'so_ensaio';
}

/** Por que uma fase não pode rodar — o código que a API e o daemon devolvem. */
export type RecusaDaFase =
  | 'rotina_sem_versao_aprovada'
  | 'rotina_quebrada_no_alvo'
  | 'rotina_nao_se_aplica_ao_alvo'
  | 'so_leitura_neste_estado'
  | 'ato_externo_fora_da_fase_1';

/**
 * Esta fase pode rodar com a rotina NESTE estado? (F3-desenho §2, D34)
 *
 * - `quebrada`, `nao_se_aplica` e `sem_versao_aprovada`: nada;
 * - `suspeita` e `so_ensaio`: só fase de `LEITURA` — é ela que confere de novo,
 *   e é com ela que o primeiro real conferido nasce;
 * - `validada`: leitura e escrita interna;
 * - **ato externo: nunca, na fase 1** (D34). O envio real exige fila por alvo,
 *   ponto sem retorno e dedup provados, que a fase 1 não tem; o ensaio de um
 *   ato é uma fase de LEITURA própria da rotina.
 */
export function podeRodar(
  estado: EstadoNoAlvo,
  classe: ClasseDeEfeito,
): { ok: true } | { ok: false; codigo: RecusaDaFase } {
  if (classe === 'ATO_EXTERNO') return { ok: false, codigo: 'ato_externo_fora_da_fase_1' };
  switch (estado) {
    case 'sem_versao_aprovada':
      return { ok: false, codigo: 'rotina_sem_versao_aprovada' };
    case 'quebrada':
      return { ok: false, codigo: 'rotina_quebrada_no_alvo' };
    case 'nao_se_aplica':
      return { ok: false, codigo: 'rotina_nao_se_aplica_ao_alvo' };
    case 'suspeita':
    case 'so_ensaio':
      return classe === 'LEITURA' ? { ok: true } : { ok: false, codigo: 'so_leitura_neste_estado' };
    case 'validada':
      return { ok: true };
    default: {
      const nenhum: never = estado;
      return nenhum;
    }
  }
}
